import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const { t } = useLang();
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState("polling");
  const sessionId = params.get("session_id");
  useDocumentTitle(t("pay_success"));

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refreshUser(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (tries >= 12) { setState("timeout"); return; }
      setTimeout(tick, 2000);
    };
    tick();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-background"><Header />
      <div className="mx-auto flex max-w-md flex-col items-center px-5 py-24 text-center">
        {state === "polling" && <><Loader2 className="h-12 w-12 animate-spin text-primary" /><p className="mt-4 text-lg font-semibold">{t("processing")}</p></>}
        {state === "paid" && (<>
          <CheckCircle2 className="h-16 w-16 text-emerald-600" data-testid="pay-success-icon" />
          <h1 className="mt-4 font-display text-3xl font-bold text-primary">{t("pay_success")}</h1>
          <p className="mt-2 text-muted-foreground">{t("pay_success_sub")}</p>
        </>)}
        {(state === "error" || state === "timeout") && (<>
          <XCircle className="h-16 w-16 text-destructive" />
          <h1 className="mt-4 font-display text-2xl font-bold text-primary">{t("processing")}</h1>
        </>)}
        <Button data-testid="back-to-journey" className="mt-8 rounded-full" onClick={() => nav("/journey")}>{t("back_to_app")}</Button>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const nav = useNavigate();
  useDocumentTitle(t("pay_cancel"));
  return (
    <div className="min-h-screen bg-background"><Header />
      <div className="mx-auto flex max-w-md flex-col items-center px-5 py-24 text-center">
        <XCircle className="h-16 w-16 text-muted-foreground" />
        <h1 className="mt-4 font-display text-3xl font-bold text-primary">{t("pay_cancel")}</h1>
        <Button className="mt-8 rounded-full" onClick={() => nav("/journey")}>{t("back_to_app")}</Button>
      </div>
    </div>
  );
}
